import { Browser } from "@capacitor/browser";
import { HUNTER_URL } from "Components/Hunter/hunterUrl";
import {
  isHunterNativeAvailable,
  openHunterNative,
} from "Infrastructure/Capacitor/HunterNative";
import { openHunterTwa } from "Infrastructure/Twa/openHunterTwa";
import { isAndroidTwa, isNativePlatform } from "Utils/Platform/PlatformUtils";

// Hunter's framing headers (frame-ancestors / X-Frame-Options) block any iframe embed,
// so each platform opens hunter as a top-level document: the native shells (iOS and the
// Capacitor Android app) via the Hunter plugin's chromeless WebView — with a browser
// fallback for app builds that predate the plugin — the legacy Android TWA via an
// intent:// launch of HunterActivity, and plain browsers in a new tab. Shared by the
// menu item and the dashboard banner. Structurally a subtype of ApplicationLink's
// link-or-action union, so the menu item can spread it.
type HunterLinkTarget =
  | { url: string; isExternalLink: true; onClick?: never }
  | { onClick: () => void; url?: never; isExternalLink?: never };

// Native shells: prefer the chromeless Hunter plugin WebView (HunterPlugin.swift in the
// moone-ios repo, HunterPlugin.java in the Android shell). App builds that predate the
// plugin (it ships via the stores) degrade to the in-app browser sheet instead of a
// silent no-op; window.open is the last resort (the shells externalize foreign hosts to
// the system browser), so the tap always produces a visible result.
const openHunterNativeShell = () => {
  if (isHunterNativeAvailable()) {
    // Transient rejections (e.g. re-present while the hunter view is mid-dismissal)
    // are swallowed — retrying the tap works; a browser fallback here would replace
    // the intended native surface over a hiccup.
    void openHunterNative().catch(() => undefined);
    return;
  }
  void Browser.open({ url: HUNTER_URL }).catch(() => {
    window.open(HUNTER_URL, "_blank", "noopener,noreferrer");
  });
};

export const getHunterLinkTarget = (): HunterLinkTarget => {
  if (isNativePlatform()) {
    return { onClick: openHunterNativeShell };
  }

  // Legacy Android TWA installs (pre-Capacitor Play releases) — kept until those are gone.
  if (isAndroidTwa()) {
    return { onClick: openHunterTwa };
  }

  return { url: HUNTER_URL, isExternalLink: true };
};

export const openHunter = () => {
  const target = getHunterLinkTarget();

  if (target.onClick) {
    target.onClick();
    return;
  }

  window.open(target.url, "_blank", "noopener,noreferrer");
};
